import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { AuthRequired } from "@/components/auth/AuthRequired";
import { SubPage } from "@/components/profile/SettingsKit";
import { SecuritySection } from "@/components/settings/SecuritySection";
import { listIdentities, addEmailCredential, resendEmailCredential } from "@/lib/auth";
import { useCooldown } from "@/lib/useCooldown";
import { useSessionAccount } from "@/lib/useSessionAccount";
import { changePassword } from "@/lib/queries";
import { humanizeError, humanizePasswordError } from "@/lib/errorMessages";
import { Card, CardHeader, CardTitle, CardBody, Input, PasswordInput, Button } from "@/components/ui";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/_app/settings_/security")({
  head: () =>
    buildHead({ path: "/settings/security", title: "Security", noindex: true }),
  component: () => (
    <AuthRequired>
      <SecurityRoute />
    </AuthRequired>
  ),
});

// SM-811: a provider-launched user signs in through the provider and has
// no password or second factor here (SM-640). This page would offer them
// an add-email + password form the server refuses, so a deep link goes
// back to the settings home, which draws no row for it either. `replace`
// so Back does not land here again.
function SecurityRoute() {
  const { externalLaunch } = useSessionAccount();
  if (externalLaunch) return <Navigate to="/settings" replace />;
  return <SecurityPage />;
}

// SecurityPage hides the password card for accounts with no EMAIL sign-in
// method (e.g. Google-only sign-ups). They have no password to change, and
// /me/password requires a current password — so the form only errored.
function SecurityPage() {
  const { t } = useTranslation("account");
  const idsQ = useQuery({ queryKey: ["me", "identities"], queryFn: listIdentities });
  const hasPassword = (idsQ.data ?? []).some((i) => i.provider_kind === "EMAIL" && i.status !== "PENDING_VERIFICATION");
  const googleEmail = (idsQ.data ?? []).find((i) => i.provider_kind === "GOOGLE")?.email;
  const pendingEmail = (idsQ.data ?? []).find((i) => i.status === "PENDING_VERIFICATION")?.email;
  return (
    <SubPage
      title={t("security.title")}
      subtitle={hasPassword ? t("security.subtitleWithPassword") : t("security.subtitleNoPassword")}
    >
      <div className="space-y-4">
        {hasPassword ? <PasswordCard /> : idsQ.isSuccess ? <AddEmailCard defaultEmail={googleEmail} pendingEmail={pendingEmail} /> : null}
        <SecuritySection />
      </div>
    </SubPage>
  );
}

function PasswordCard() {
  const { t } = useTranslation("account");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const m = useMutation({
    mutationFn: () => changePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
    onError: (e) => setErr(humanizePasswordError(e)),
  });
  const submit = () => {
    setErr(null);
    setOk(false);
    if (next.length < 8) return setErr(t("security.passwordTooShort"));
    if (next !== confirm) return setErr(t("form.passwordsDontMatch"));
    m.mutate();
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("form.changePassword")}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="space-y-3">
          <PasswordInput placeholder={t("form.currentPassword")} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          <PasswordInput placeholder={t("form.newPassword")} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          <PasswordInput placeholder={t("form.confirmNewPassword")} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          {err && <p className="text-sm text-danger">{err}</p>}
          {ok && <p className="text-sm text-success">{t("security.passwordUpdated")}</p>}
          <Button onClick={submit} loading={m.isPending} disabled={!current || !next}>
            {t("form.updatePassword")}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// AddEmailCard lets a social-only account (e.g. Google sign-up) add an
// email + password sign-in method. Submitting emails a verification link;
// the credential is created only after the user confirms it, so the form
// ends on a "check your email" state rather than an immediate success.
function AddEmailCard({ defaultEmail, pendingEmail }: { defaultEmail?: string; pendingEmail?: string }) {
  const { t } = useTranslation("account");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const cooldown = useCooldown();
  const refresh = () => qc.invalidateQueries({ queryKey: ["me", "identities"] });
  const initiate = useMutation({
    mutationFn: () => addEmailCredential({ email: email.trim(), password }),
    onSuccess: () => {
      cooldown.start(60);
      refresh();
    },
    onError: (e) => setErr(humanizeError(e)),
  });
  const resend = useMutation({
    mutationFn: () => resendEmailCredential(),
    onSuccess: () => {
      cooldown.start(60);
      refresh();
    },
    onError: (e) => setErr(humanizeError(e)),
  });
  const submit = () => {
    setErr(null);
    if (!email.trim()) return setErr(t("security.addEmail.enterEmail"));
    if (password.length < 8) return setErr(t("security.addEmail.passwordTooShort"));
    if (password !== confirm) return setErr(t("form.passwordsDontMatch"));
    initiate.mutate();
  };
  // A pending bind (from the identities list) drives the card into its
  // "check your email / resend" state — so it survives reloads and can't be
  // re-requested from a fresh form (which would pile up duplicate links).
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("security.addEmail.title")}</CardTitle>
      </CardHeader>
      <CardBody>
        {pendingEmail ? (
          <div className="space-y-3">
            <p className="text-sm text-fg-muted">
              <Trans
                t={t}
                i18nKey="security.addEmail.pendingBody"
                values={{ email: pendingEmail }}
                components={{ strong: <span className="font-medium text-fg" /> }}
              />
            </p>
            {err && <p className="text-sm text-danger">{err}</p>}
            <Button variant="outline" disabled={cooldown.active} loading={resend.isPending} onClick={() => resend.mutate()}>
              {cooldown.active
                ? t("security.addEmail.resendIn", { seconds: cooldown.remaining })
                : t("security.addEmail.resendLink")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-fg-muted">{t("security.addEmail.intro")}</p>
            <Input type="email" placeholder={t("form.emailAddress")} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            <PasswordInput placeholder={t("form.newPassword")} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            <PasswordInput placeholder={t("form.confirmPassword")} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            {err && <p className="text-sm text-danger">{err}</p>}
            <Button onClick={submit} loading={initiate.isPending} disabled={!email.trim() || !password}>
              {t("security.addEmail.sendVerification")}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
